import { takeEvery, all, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { SweetAlertResult } from 'sweetalert2';
import moment from 'moment';
import apiService from 'app/bff/services/apiService';
import { UserIdentityTypesEnum } from 'app/bff/enums/user';
import { AaGetcaptchaResp } from 'app/bff/models/aa/aaGetcaptcha';
import { AaOtpVerifyResp } from 'app/bff/models/aa/aaOtpVerify';
import { MbSigninResp } from 'app/bff/models/mb/mbSignin';
import { MbSignoutResp } from 'app/bff/models/mb/mbSignout';
import { ROUTES } from 'app/core/router/paths';
import commonService from 'app/core/services/commonService';
import storageService from 'app/core/services/storageService';
import modalService from 'app/core/services/modalService';
import { StorageKeysEnum } from 'app/core/enums/storage';
import { ProcessRouteMatchesStep as ReauthProcessRouteMatchesStep } from 'app/features/Member/Reauth/types';
import { MobileLoginProcessStepCodesEnum } from 'app/common/components/Modals/MemberLoginModal/types';
import { RootState } from 'app/store/types';
import { sagaBoundary } from 'app/store/service';
import { saveMemberAuthorizationAction, resetMemberCacheAction } from 'app/store/common/actions';
import { saveMemberProfileAction, setAccessibleStepAction } from 'app/store/features/member/reauth/actions';
import {
  LoginState,
  MEMBER_LOGIN__BY_PASS_PHRASE_LOGIN,
  MEMBER_LOGIN__FETCH_CAPTCHA,
  MEMBER_LOGIN__FETCH_MEMBER_PROFILE_BY_MOBILE_LOGIN,
  MEMBER_LOGIN__RESEND_OTP,
  MEMBER_LOGIN__LOGIN_BY_OTP,
  MEMBER_LOGIN__SEND_LOGOUT,
  LoginByMobileLoginAction,
  LoginByPassPhraseLoginAction,
  LoginByOTPAction,
  SendLogoutAction
} from './types';
import {
  fetchCaptchaDoneAction,
  saveMobileLoginDataAction,
  sendOTPDoneAction,
  setMobileLoginCurrentStepAction,
  resendOTPDoneAction,
  resetLoginCacheAction
} from './actions';
import { SystemMessageIdEnum } from 'app/bff/enums/system';
import { AaOtpSendcodeResp, AaOtpSendcodeReq } from 'app/bff/models/aa/aaOtpSendcode';
import store from 'app/store/index';
import { OTPActionsEnum } from 'app/bff/enums/otp';
import { MbGetinfoResp } from 'app/bff/models/mb/mbGetinfo';

/**
 * @description 取得圖形驗證碼
 */
function * fetchCaptcha () {
  const response: AaGetcaptchaResp = yield call(apiService.postAaGetcaptcha, {
    unsecure: null
  });
  if (response) yield put(fetchCaptchaDoneAction(response));
}

/**
 * @description 入口 (手機驗證登入)
 */
function * loginByMobileLogin (action: LoginByMobileLoginAction) {
  const response: MbSigninResp = yield call(apiService.postMbSignin, {
    data: action.payload.data,
    captcha: action.payload.checkCaptchaArgs
  });
  if (response.userType === UserIdentityTypesEnum.NA || response.userType === UserIdentityTypesEnum.NS) {
    // 使用者身份「僅為網服會員」或「非會員」，則引導使用者註冊
    const result: SweetAlertResult = yield modalService.base('系統提醒', '登入錯誤，帳號或是密碼錯誤', '確定');
    if (result.value && result.isConfirmed) {
      // 關閉「會員登入」Modal
      modalService.login(false);
    }
  }
  // 使用者身份「僅網投會員」或「網服、網投會員」
  if (response.userType === UserIdentityTypesEnum.BH || response.userType === UserIdentityTypesEnum.OIS) {
    // 緩存表單資料
    yield put(
      saveMobileLoginDataAction({
        data: action.payload.data,
        captcha: action.payload.checkCaptchaArgs
      })
    );
    // 緩存 OTP 資料
    yield put(sendOTPDoneAction(response));
    // 前往下一步 (Step-2)
    yield put(setMobileLoginCurrentStepAction(MobileLoginProcessStepCodesEnum.OTPAuth));
  }
}

/**
 * @description 入口 (密碼登入)
 */
function * loginByPassPhraseLogin (action: LoginByPassPhraseLoginAction) {
  // 使用密碼登入 (驗證會員帳號密碼)
  const response: MbSigninResp = yield call(apiService.postMbSignin, {
    captcha: action.payload.checkCaptchaArgs,
    data: action.payload.authMemberArgs
  });
  // 使用者身份「僅為網服會員」或「非會員」，則引導使用者註冊
  if (response.userType === UserIdentityTypesEnum.NA || response.userType === UserIdentityTypesEnum.NS) {
    const result: SweetAlertResult = yield modalService.base('系統提醒', '登入錯誤，帳號或是密碼錯誤', '確定');
    if (result.value && result.isConfirmed) {
      // 關閉「會員登入」Modal
      modalService.login(false);
    }
  }
  // 使用者身份「僅網投會員」或「網服、網投會員」
  if (response.userType === UserIdentityTypesEnum.BH || response.userType === UserIdentityTypesEnum.OIS) {
    // 重新生成 Session Id
    commonService.generateSessionId(true);
    // 重置登入流程資料緩存
    yield put(resetLoginCacheAction());
    // 檢查超過五年未投保狀態 (超過五年：進入重新驗證身分流程；未超過五年：發送 OTP)
    const memberInfo: MbGetinfoResp = yield call(apiService.postMbGetinfo, { users: { TOKEN: response.token } });
    const decryptedMemberInfo = commonService.decryptMemberInfo(memberInfo);
    const { lastInsAppTime, revalidTime, regTime } = decryptedMemberInfo.USERS;
    if (
      // 無最新保單日期、無重新身分驗證紀錄、註冊時間大於五年
      (!lastInsAppTime && !revalidTime && moment().diff(regTime, 'y') >= 5) ||
      // 無最新保單日期、重新身分驗證紀錄大於五年、註冊時間大於五年
      (!lastInsAppTime && revalidTime && moment().diff(revalidTime, 'y') >= 5 && regTime && moment().diff(regTime, 'y') >= 5) ||
      // 最新保單日期大於五年、無重新身分驗證紀錄、註冊時間大於五年
      (lastInsAppTime &&
        moment().diff(lastInsAppTime, 'y') >= 5 &&
        !revalidTime &&
        regTime &&
        moment().diff(regTime, 'y') >= 5) ||
      // 最新保單日期大於五年、重新身分驗證紀錄大於五年、註冊時間大於五年
      (lastInsAppTime &&
        moment().diff(lastInsAppTime, 'y') >= 5 &&
        revalidTime &&
        moment().diff(revalidTime, 'y') >= 5 &&
        regTime &&
        moment().diff(regTime, 'y') >= 5)
    ) {
      const result: SweetAlertResult = yield modalService.confirm(
        '提醒',
        '你已超過5年無本公司投保紀錄，請重新驗證身份。',
        '重新驗證',
        '回首頁'
      );
      if (result.isConfirmed) {
        yield put(saveMemberProfileAction(decryptedMemberInfo));
        // 將重新驗證身分流程，「同意相關告知事項」(Step-1) 設為可訪問步驟
        yield put(setAccessibleStepAction(ReauthProcessRouteMatchesStep[ROUTES.MEMBER__REAUTH__CLAUSES]));
        // 導頁至重新驗證身分流程，「同意相關告知事項」(Step-1)
        yield put(push(ROUTES.MEMBER__REAUTH__CLAUSES));
        // 關閉「會員登入」Modal
        modalService.login(false);
      } else {
        yield put(push(ROUTES.HOME__MAIN));
        // 關閉「會員登入」Modal
        modalService.login(false);
      }
    } else {
      // 緩存「會員主檔」和「Access Token」至 browser storage
      storageService.setItem(StorageKeysEnum.Authorization, JSON.stringify(response));
      // 緩存「會員主檔」和「Access Token」至 store
      yield put(saveMemberAuthorizationAction(response));
      const loginState: LoginState = yield select((state: RootState) => state.features.member.login);
      // 執行 Route redirect
      if (loginState.redirectURL) yield put(push(loginState.redirectURL, loginState.redirectRouteState));
      // 檢驗是否需要變更密碼
      const noChangePassWord = store
        .getState()
        .common.systemMessages.find((item) => item.msgType === 'UI' && item.msgId === SystemMessageIdEnum.W009);
      if (decryptedMemberInfo.USERS.LAST_CHANG_PASSWORD_DATE) {
        if (moment().diff(decryptedMemberInfo.USERS.LAST_CHANG_PASSWORD_DATE, 'y') >= 1) {
          const result: SweetAlertResult = yield modalService.confirm(
            noChangePassWord?.msgSubject ?? '提醒',
            noChangePassWord?.msgContent ?? ' 您已一年未變更密碼，為了保護您的資料安全，建議您變更會員密碼',
            '變更密碼',
            '暫不變更'
          );
          if (result.isConfirmed) yield put(push(ROUTES.MEMBER__SETTINGS__PASS_PHRASE__AUTH));
        }
      } else {
        if (moment().diff(decryptedMemberInfo.USERS.regTime, 'y') >= 1) {
          const result: SweetAlertResult = yield modalService.confirm(
            noChangePassWord?.msgSubject ?? '提醒',
            noChangePassWord?.msgContent ?? ' 您已一年未變更密碼，為了保護您的資料安全，建議您變更會員密碼',
            '變更密碼',
            '暫不變更'
          );
          if (result.isConfirmed) yield put(push(ROUTES.MEMBER__SETTINGS__PASS_PHRASE__AUTH));
        }
      }
      // 關閉「會員登入」Modal
      modalService.login(false);
    }
  }
}

/**
 * @description 重新發送 OTP 驗證碼
 */
function * resendOTP () {
  const loginState: LoginState = yield select((state: RootState) => state.features.member.login);
  const request: AaOtpSendcodeReq = {
    action: OTPActionsEnum.FrontendLogin,
    emailAddr: loginState.mobileLoginProcess.otp?.emailAddr,
    mobile: loginState.mobileLoginProcess.otp?.mobile,
    memberId: loginState.mobileLoginProcess.data?.data.memberId ?? ''
  };
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, request);
  if (response) {
    // 更新 OTP 資料
    yield put(
      resendOTPDoneAction({
        ...loginState.mobileLoginProcess.otp,
        userType: loginState.mobileLoginProcess.otp?.userType ?? '',
        emailAddr: response.emailAddr,
        mobile: response.mobile,
        sn: response.sn,
        expireTime: response.expireTime
      })
    );
  }
}

/**
 * @description 使用 OTP 驗證碼登入 (驗證 OTP 驗證碼)
 */
function * loginByOTP (action: LoginByOTPAction) {
  const response: AaOtpVerifyResp = yield call(apiService.postAaOtpVerify, action.payload.args);
  if (response) {
    const loginState: LoginState = yield select((state: RootState) => state.features.member.login);
    // 重新生成 Session Id
    commonService.generateSessionId(true);
    // 重置登入流程資料緩存
    yield put(resetLoginCacheAction());
    // 檢查超過五年未投保狀態 (超過五年：進入重新驗證身分流程；未超過五年：發送 OTP)
    const memberInfo: MbGetinfoResp = yield call(apiService.postMbGetinfo, { users: { TOKEN: response.token } });
    const decryptedMemberInfo = commonService.decryptMemberInfo(memberInfo);
    const { lastInsAppTime, revalidTime, regTime } = decryptedMemberInfo.USERS;
    if (
      // 無最新保單日期、無重新身分驗證紀錄、註冊時間大於五年
      (!lastInsAppTime && !revalidTime && moment().diff(regTime, 'y') >= 5) ||
      // 無最新保單日期、重新身分驗證紀錄大於五年、註冊時間大於五年
      (!lastInsAppTime && revalidTime && moment().diff(revalidTime, 'y') >= 5 && regTime && moment().diff(regTime, 'y') >= 5) ||
      // 最新保單日期大於五年、無重新身分驗證紀錄、註冊時間大於五年
      (lastInsAppTime &&
        moment().diff(lastInsAppTime, 'y') >= 5 &&
        !revalidTime &&
        regTime &&
        moment().diff(regTime, 'y') >= 5) ||
      // 最新保單日期大於五年、重新身分驗證紀錄大於五年、註冊時間大於五年
      (lastInsAppTime &&
        moment().diff(lastInsAppTime, 'y') >= 5 &&
        revalidTime &&
        moment().diff(revalidTime, 'y') >= 5 &&
        regTime &&
        moment().diff(regTime, 'y') >= 5)
    ) {
      const result: SweetAlertResult = yield modalService.confirm(
        '提醒',
        '你已超過5年無本公司投保紀錄，請重新驗證身份。',
        '重新驗證',
        '回首頁'
      );
      if (result.isConfirmed) {
        // 緩存會員資料至重新驗證身分流程
        yield put(saveMemberProfileAction(decryptedMemberInfo));
        // 將重新驗證身分流程，「同意相關告知事項」(Step-1) 設為可訪問步驟
        yield put(setAccessibleStepAction(ReauthProcessRouteMatchesStep[ROUTES.MEMBER__REAUTH__CLAUSES]));
        // 導頁至重新驗證身分流程，「同意相關告知事項」(Step-1)
        yield put(push(ROUTES.MEMBER__REAUTH__CLAUSES));
        // 關閉「會員登入」Modal
        modalService.login(false);
      } else {
        yield put(push(ROUTES.HOME__MAIN));
        // 關閉「會員登入」Modal
        modalService.login(false);
      }
    } else {
      // 緩存「會員主檔」和「Access Token」至 browser storage
      storageService.setItem(StorageKeysEnum.Authorization, JSON.stringify(response));
      // 緩存「會員主檔」和「Access Token」至 store
      yield put(saveMemberAuthorizationAction(response));
      // 執行 Route redirect
      if (loginState.redirectURL) yield put(push(loginState.redirectURL, loginState.redirectRouteState));
      // 檢驗是否需要變更密碼
      const noChangePassWord = store
        .getState()
        .common.systemMessages.find((item) => item.msgType === 'UI' && item.msgId === SystemMessageIdEnum.W009);
      if (decryptedMemberInfo.USERS.LAST_CHANG_PASSWORD_DATE) {
        if (moment().diff(decryptedMemberInfo.USERS.LAST_CHANG_PASSWORD_DATE, 'y') >= 1) {
          const result: SweetAlertResult = yield modalService.confirm(
            noChangePassWord?.msgSubject ?? '提醒',
            noChangePassWord?.msgContent ?? ' 您已一年未變更密碼，為了保護您的資料安全，建議您變更會員密碼',
            '變更密碼',
            '暫不變更'
          );
          if (result.isConfirmed) yield put(push(ROUTES.MEMBER__SETTINGS__PASS_PHRASE__AUTH));
        }
      } else {
        if (moment().diff(decryptedMemberInfo.USERS.regTime, 'y') >= 1) {
          const result: SweetAlertResult = yield modalService.confirm(
            noChangePassWord?.msgSubject ?? '提醒',
            noChangePassWord?.msgContent ?? ' 您已一年未變更密碼，為了保護您的資料安全，建議您變更會員密碼',
            '變更密碼',
            '暫不變更'
          );
          if (result.isConfirmed) yield put(push(ROUTES.MEMBER__SETTINGS__PASS_PHRASE__AUTH));
        }
      }
      // 關閉「會員登入」Modal
      modalService.login(false);
    }
  }
}

/**
 * @description 發送會員登出
 */
function * sendLogout (action: SendLogoutAction) {
  const response: MbSignoutResp = yield call(apiService.postMbSignout, action.payload.args);
  if (response) {
    // 重新生成 Session Id
    commonService.generateSessionId(true);
    // 清除「會員主檔」和「Access Token」
    storageService.removeItem(StorageKeysEnum.Authorization);
    // 重置會員相關資料緩存
    yield put(resetMemberCacheAction());
  }
}

export default function * watchLoginnSaga () {
  yield all([
    takeEvery(MEMBER_LOGIN__FETCH_CAPTCHA, sagaBoundary(fetchCaptcha)),
    takeEvery(MEMBER_LOGIN__FETCH_MEMBER_PROFILE_BY_MOBILE_LOGIN, sagaBoundary(loginByMobileLogin)),
    takeEvery(MEMBER_LOGIN__BY_PASS_PHRASE_LOGIN, sagaBoundary(loginByPassPhraseLogin)),
    takeEvery(MEMBER_LOGIN__RESEND_OTP, sagaBoundary(resendOTP)),
    takeEvery(MEMBER_LOGIN__LOGIN_BY_OTP, sagaBoundary(loginByOTP)),
    takeEvery(MEMBER_LOGIN__SEND_LOGOUT, sagaBoundary(sendLogout))
  ]);
}
